/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable no-underscore-dangle */
import React, { Dispatch, FC, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Form, Formik, FormikProps } from 'formik';
import { styled } from 'linaria/react';
import { useToast } from '@mtsbank/ui-kit';
import { PayeeWithCustomParamsType, useCyberStore } from '@entities/tsp';
import { CommissionDataByServiceIdType } from '@entities/denomination';
import { PaymentFormValues, PaymentMethod } from '@entities/payment';
import { ServerOption } from '@entities/server';
import { findDenominationByServiceId, GetPriceResp, useGetPrice } from '@entities/voucher';
import { FetchHintsResponseType } from '@entities/hint';
import { generateUuid } from '@shared/lib';
import { Partners, usePartner } from '@shared/lib/partner';
import { MESSAGE } from '@shared/config';
import { Spacer } from '@shared/ui/atoms';
import { Param, Payee } from '@root/api/ump-catalog-manager';
import { TYPE_OF_FIELDS_TO_BE_MAPPED } from '../config/fieldsParams';
import { initialPaymentFormValues } from '../config/form';
import { getPurchaseFormValidation } from '../config/getPurchaseFormValidation';
import { useGetCommissions } from '../model/useGetCommissions';
import { usePayBySBP } from '../model/usePayBySBP';
import { usePayByMtsAccount } from '../model/usePayByMtsAccount';
import { PROMOCODES_FROM_2K, PUBG_GENSHIN_WIDGET_PROMOODE } from '../../../../entities/promocode/config/promocodes';
import { usePayByCard } from '../model/usePayByCard';
interface TspInfoProps {
  title: string;
  icon?: string;
  showVouchersForm?: boolean;
  params?: Param[];
  activeService?: Payee;
}
interface ReplenishmentInfoProps {
  isSteam: boolean;
  countryVouchers: {
    country: string;
    icon: string;
  }[];
  denominationsByCountry: Record<string, Payee[]>;
  activeService: Payee;
  showVouchersForm: boolean;
  setShowVouchersForm: (showVouchersForm: boolean) => void;
  hints: FetchHintsResponseType;
}
interface AccountInfoProps {
  showVouchersForm: boolean;
  activeService: Payee;
  priceData?: GetPriceResp;
  servers: ServerOption[];
  getCommissions: (baseAmount: number) => void;
}
interface PaymentInfoProps {
  payee: Payee;
  linkedTsp: Payee;
  priceData: GetPriceResp;
  countryVouchers: {
    country: string;
    icon: string;
  }[];
  denominationsByCountry: Record<string, Payee[]>;
  showVouchersForm?: boolean;
  isPaymentloading: boolean;
  isAmountInfoLoading: boolean;
  cancelGetPaymentTermsRequest: () => void;
  cancelPriceRequest: () => void;
  getCommissions: (base: number) => void;
}
interface SbpQrModalProps {
  payee: Payee;
  open: boolean;
  handleClose: () => void;
  qrLink: string;
  description: string;
  showVouchersForm: boolean;
}
interface PaymentFormProps {
  showVouchersForm: boolean;
  setShowVouchersForm: Dispatch<SetStateAction<boolean>>;
  h1: string;
  payee: PayeeWithCustomParamsType;
  linkedTsp: PayeeWithCustomParamsType;
  countryVouchers: {
    country: string;
    icon: string;
  }[];
  denominationsByCountry: Record<string, Payee[]>;
  servers: ServerOption[];
  hints: FetchHintsResponseType;
  commissionData: CommissionDataByServiceIdType;
  renderTspInfo: (props: TspInfoProps) => ReactNode;
  renderReplenishmentInfo: (props: ReplenishmentInfoProps) => ReactNode;
  renderAccountInfo: (props: AccountInfoProps) => ReactNode;
  renderPaymentInfo: (props: PaymentInfoProps) => ReactNode;
  renderQrModal: (props: SbpQrModalProps) => ReactNode;
}
const PaymentForm: FC<PaymentFormProps> = ({
  showVouchersForm,
  setShowVouchersForm,
  h1,
  payee,
  linkedTsp,
  countryVouchers,
  denominationsByCountry,
  servers,
  hints,
  commissionData,
  renderTspInfo,
  renderReplenishmentInfo,
  renderAccountInfo,
  renderPaymentInfo,
  renderQrModal
}) => {
  const [id, setId] = useState<string>(generateUuid());
  const router = useRouter();
  const paymentFormRef = useRef<FormikProps<PaymentFormValues>>(null);
  const {
    toast
  } = useToast();
  const {
    partner
  } = usePartner();
  const {
    paymentMethod,
    isPaymentloading,
    isAmountInfoLoading,
    activeCountry,
    setActiveCountry,
    activeDenomination,
    setActiveDenomination,
    setAmountInfo,
    setRequestID,
    setIsPromocodeActivated,
    lastActivatedPromocode,
    setLastActivatedPromocode
  } = useCyberStore();
  const {
    getCommissions,
    isGetPaymentTermsLoading,
    cancelGetPaymentTermsRequest
  } = useGetCommissions({
    // @ts-ignore
    isVouchers,
    payee,
    // @ts-ignore
    linkedTsp,
    paymentFormRef,
    commissionData
  });
  const {
    getPrice,
    cancelPriceRequest,
    isGetPriceLoading,
    isPriceError,
    priceData,
    activeDenominationParams
  } =
  // @ts-ignore
  useGetPrice(activeDenomination);

  // @ts-ignore
  const {
    qrLink,
    payBySBP,
    cancelSBPPayment
  } = usePayBySBP({
    isVouchers,
    payee,
    id,
    setId
  });
  // @ts-ignore
  const {
    payByMtsAccount,
    isPaymentByMtsAccountLoading
  } = usePayByMtsAccount({
    isVouchers,
    payee
  });
  const {
    payByCard,
    isPaymentByCardLoading
  } = usePayByCard({
    showVouchersForm,
    payee
  });
  useEffect(() => {
    setRequestID(id);
  }, [id, setRequestID]);
  useEffect(() => {
    setIsPromocodeActivated(false);
    setLastActivatedPromocode('');
    paymentFormRef.current?.setFieldValue('promocode', '');
  }, [payee]);
  useEffect(() => {
    // @ts-ignore
    if (payee.features.tsp_url === 'steam' && showVouchersForm) {
      setIsPromocodeActivated(false);
      setLastActivatedPromocode('');
    }
  }, [showVouchersForm]);

  // инициализация активной страны при маунте/переходе на стр другого сервиса
  useEffect(() => {
    // if (!router.isReady) return

    // if (router.query.serviceId) {
    //   const denomination = findDenominationByServiceId(countriesVouchers, router.query.serviceId as string)

    //   setActiveCountry((denomination.features?.country_web || 'noRegion') as string)
    // } else {
    //   setActiveCountry(countryVouchers?.[0]?.country)
    // }
    setActiveCountry(countryVouchers?.[0]?.country);
    setShowVouchersForm(payee?.features?.merchant_association_for_WEB ? !payee?.features?.merchant_association_for_WEB : !!payee?.features || !!payee?.features?.screenType);
  }, [countryVouchers, payee]);

  // инициализация активного номинала при маунте/выборе страны
  useEffect(() => {
    cancelGetPaymentTermsRequest();

    // if (!router.isReady) return

    // if (router.query.serviceId) {
    //   const denomination = findDenominationByServiceId(countriesVouchers, router.query.serviceId as string)

    //   setActiveDenomination(countriesVouchers?.[activeCountry]?.[0]?.features?.buttonLabel ? denomination : null)
    // } else {
    setActiveDenomination(denominationsByCountry?.[activeCountry as string]?.[0]?.features?.buttonLabel ? denominationsByCountry?.[activeCountry as string]?.[0] : null);
    // }
  }, [showVouchersForm, activeCountry, payee]);

  // получение цены при выборе номинала
  useEffect(() => {
    if (showVouchersForm) {
      setAmountInfo(null);
      if (activeDenomination && activeDenominationParams) {
        cancelGetPaymentTermsRequest();
        getPrice();
      }
    }
  }, [activeDenomination, showVouchersForm]);
  useEffect(() => {
    if (isPriceError) {
      toast('error', isPriceError.response?.data?.partnerMessage as string || MESSAGE, '');
    }
  }, [isPriceError]);
  const handleServiceFormSubmitData = (values: PaymentFormValues) => {
    if (isAmountInfoLoading) return;
    if (values.promocode.toUpperCase() === PUBG_GENSHIN_WIDGET_PROMOODE && (
    // @ts-ignore
    activeDenomination.serviceId === '24568' ||
    // @ts-ignore
    activeDenomination.serviceId === '23254' ||
    // @ts-ignore
    activeDenomination.serviceId === '25862')) {
      setIsPromocodeActivated(false);
      // @ts-ignore
      paymentFormRef.current.setFieldError('promocode', 'Данный номинал не участвует в розыгрыше');
      return;
    }
    if (PROMOCODES_FROM_2K?.includes(lastActivatedPromocode) && Number(values.price) < 2000) {
      setIsPromocodeActivated(false);
      // @ts-ignore
      paymentFormRef.current.setFieldError('promocode', 'Промокод действует только при пополнении от 2.000 ₽');
      return;
    }
    const isFromPubgGiveawayPage = router.query.from === 'rozygrysh-prizov-pubg-mobile';
    if (isFromPubgGiveawayPage) {
      window.recordTier?.push({
        eventName: 'landingpageClick',
        tracking_id: '128635520'
      });
      window._tmr?.push({
        type: 'reachGoal',
        id: 3712315,
        goal: 'click_purchase'
      });
    }
    if (partner === Partners.DEFAULT) {
      window?._tmr?.push({
        type: 'reachGoal',
        id: 3712315,
        goal: 'click_pay_games_cyber'
      });
    }
    if (paymentMethod === PaymentMethod.SBP) {
      payBySBP(values);
    } else if (paymentMethod === PaymentMethod.CARD) {
      payByCard();
    } else {
      payByMtsAccount();
    }
  };
  const fields = payee?.params?.filter(param => TYPE_OF_FIELDS_TO_BE_MAPPED.includes(param.name));
  return <Formik enableReinitialize validateOnBlur={false} validateOnChange={false} innerRef={paymentFormRef} initialValues={initialPaymentFormValues} validationSchema={getPurchaseFormValidation({
    isShowVouchers: showVouchersForm,
    // @ts-ignore
    fields,
    partner,
    paymentMethod
  })} onSubmit={handleServiceFormSubmitData}>
      <StyledForm>
        {renderQrModal({
        payee,
        open: !!qrLink && paymentMethod === PaymentMethod.SBP,
        qrLink,
        description: h1,
        handleClose: cancelSBPPayment,
        showVouchersForm
      })}
        <WrapperServicesForm>
          <LeftContent>
            {renderTspInfo({
            title: h1,
            icon: payee?.features?.tsp_picture_web,
            showVouchersForm,
            params: linkedTsp ? linkedTsp?.params : payee?.params,
            activeService: payee
          })}

            <Spacer space={16} spacetablet={16} spacemob={12} />
            <>
              {renderReplenishmentInfo({
              isSteam: !!payee?.features?.merchant_association_for_WEB,
              countryVouchers,
              denominationsByCountry,
              activeService: payee,
              showVouchersForm,
              setShowVouchersForm,
              hints
            })}
              <Spacer space={16} spacetablet={16} spacemob={12} />
              {renderAccountInfo({
              showVouchersForm,
              activeService: payee,
              priceData,
              servers,
              // @ts-ignore
              getCommissions
            })}
            </>
          </LeftContent>
          <RightContent partner={partner}>
            {renderPaymentInfo({
            showVouchersForm,
            payee,
            linkedTsp,
            // @ts-ignore
            priceData,
            countryVouchers,
            denominationsByCountry,
            isPaymentloading: isPaymentloading || isPaymentByMtsAccountLoading || isPaymentByCardLoading,
            isAmountInfoLoading: isAmountInfoLoading || isGetPaymentTermsLoading || isGetPriceLoading,
            cancelGetPaymentTermsRequest,
            cancelPriceRequest,
            // @ts-ignore
            getCommissions
          })}
          </RightContent>
        </WrapperServicesForm>
      </StyledForm>
    </Formik>;
};
export default PaymentForm;
const WrapperServicesForm = /*#__PURE__*/styled('div')({
  name: "WrapperServicesForm",
  class: "w1lxbhr3",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => Form;
const StyledForm = /*#__PURE__*/styled(_exp())({
  name: "StyledForm",
  class: "sp6a2zs",
  propsAsIs: true
});
const LeftContent = /*#__PURE__*/styled('div')({
  name: "LeftContent",
  class: "lvb3xfs",
  propsAsIs: false
});
interface RightContentProps {
  partner: Partners;
}
const _exp2 = /*#__PURE__*/() => ({
  partner
}) => partner === Partners.SMARTSHELL ? '0' : '88px';
const RightContent = /*#__PURE__*/styled('div')({
  name: "RightContent",
  class: "r1l59ked",
  propsAsIs: false,
  vars: {
    "r1l59ked-0": [_exp2()]
  }
});

require("./PaymentForm copy.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./PaymentForm copy.tsx");